import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Grid,
  Container,
  Chip,
  alpha,
  useTheme,
  useMediaQuery,
  Avatar,
  Divider
} from "@mui/material";
import {
  FaWhatsapp,
  FaPhoneAlt,
  FaEnvelope,
  FaGlobe,
  FaMapMarkerAlt,
  FaClock,
  FaHeadset,
  FaShieldAlt,
  FaRocket
} from "react-icons/fa";
import {
  FiMessageSquare,
  FiTrendingUp,
  FiArrowUpRight
} from "react-icons/fi";
import { MdSupportAgent, MdCheckCircle } from "react-icons/md";
import apiEndpoints from "../../apiconfig";
import { useSnackbar } from "../../DynamicComponents/SnackbarContext";

const SupportPage = () => {
  const { showSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [org, setOrg] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const fetchSupportDetails = async () => {
      try {
        const res = await fetch(apiEndpoints.getSupportDetails);
        const data = await res.json();
        if (data.status === "success") {
          setOrg(data.data);
        } else {
          showSnackbar("No support details found.", "warning");
        }
      } catch (err) {
        console.error(err);
        showSnackbar("Failed to load support details.", "error");
      } finally {
        setLoading(false);
      }
    };
    fetchSupportDetails();
  }, [showSnackbar]);

  const SupportChannelCard = ({ icon, title, subtitle, action, color, gradient, badge }) => (
    <Card
      sx={{
        height: '100%',
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        borderRadius: 3,
        transition: 'all 0.3s ease',
        background: gradient,
        position: 'relative',
        overflow: 'visible',
        '&:hover': {
          transform: isMobile ? 'none' : 'translateY(-8px)',
          boxShadow: isMobile ? 'none' : `0 20px 40px ${alpha(color, 0.15)}`,
          borderColor: alpha(color, 0.3)
        }
      }}
    >
      {badge && (
        <Chip
          label={badge}
          size="small"
          sx={{
            position: 'absolute',
            top: -10,
            right: 16,
            backgroundColor: color,
            color: 'white',
            fontWeight: 700,
            fontSize: '10px',
            height: '20px'
          }}
        />
      )}
      <CardContent
        sx={{
          p: { xs: 3, sm: 4 },
          textAlign: 'center',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          cursor: 'pointer'
        }}
        onClick={action}
      >
        <Box
          sx={{
            display: 'inline-flex',
            p: { xs: 2, sm: 3 },
            borderRadius: 3,
            background: `linear-gradient(135deg, ${alpha(color, 0.2)} 0%, ${alpha(color, 0.1)} 100%)`,
            mb: 3,
            transition: 'all 0.3s ease'
          }}
        >
          {React.cloneElement(icon, {
            size: isMobile ? 28 : 32,
            color: color
          })}
        </Box>
        <Typography variant={isMobile ? "subtitle1" : "h6"} fontWeight={700} gutterBottom sx={{ color: 'text.primary' }}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, flexGrow: 1, lineHeight: 1.6, fontSize: { xs: '0.875rem', sm: '0.9rem' } }}>
          {subtitle}
        </Typography>
        <Button
          variant="outlined"
          size={isMobile ? "small" : "medium"}
          endIcon={<FiArrowUpRight />}
          sx={{
            borderColor: color,
            color: color,
            fontWeight: 600,
            borderRadius: 2,
            fontSize: { xs: '0.75rem', sm: '0.875rem' },
            '&:hover': {
              borderColor: color,
              backgroundColor: alpha(color, 0.08),
              transform: isMobile ? 'none' : 'translateX(4px)'
            },
            transition: 'all 0.2s ease'
          }}
        >
          Connect
        </Button>
      </CardContent>
    </Card>
  );

  const InfoItem = ({ icon, label, value, isLink = false, verified = false }) => (
    <Box sx={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: { xs: 2, sm: 3 },
      p: { xs: 2, sm: 3 },
      borderRadius: 2,
      backgroundColor: alpha(theme.palette.background.paper, 0.5),
      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
      transition: 'all 0.2s ease',
      '&:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.02),
        borderColor: alpha(theme.palette.primary.main, 0.2)
      }
    }}>
      <Box
        sx={{
          display: 'flex',
          p: { xs: 1.5, sm: 2 },
          borderRadius: 2,
          backgroundColor: alpha(theme.palette.primary.main, 0.1),
          flexShrink: 0
        }}
      >
        {React.cloneElement(icon, {
          size: isMobile ? 18 : 20,
          color: theme.palette.primary.main
        })}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography variant="subtitle2" fontWeight={600} color="text.primary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
            {label}
          </Typography>
          {verified && (
            <MdCheckCircle size={isMobile ? 14 : 16} color={theme.palette.success.main} />
          )}
        </Box>
        {isLink ? (
          <a
            href={value}
            target="_blank"
            rel="noreferrer"
            style={{
              color: theme.palette.primary.main,
              textDecoration: 'none',
              wordBreak: 'break-word',
              display: 'block',
              fontWeight: 500,
              fontSize: isMobile ? '0.8rem' : '0.875rem'
            }}
            onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
            onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
          >
            {value}
          </a>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ 
            wordBreak: 'break-word', 
            fontWeight: 500,
            fontSize: { xs: '0.8rem', sm: '0.875rem' }
          }}>
            {value}
          </Typography>
        )}
      </Box>
    </Box>
  );

  if (loading) {
    return (
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '80vh',
        background: 'linear-gradient(135deg, #F8FAFC 0%, #F1F5F9 100%)'
      }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress
            size={isMobile ? 40 : 60}
            thickness={4}
            sx={{
              mb: 3,
              color: theme.palette.primary.main
            }}
          />
          <Typography variant={isMobile ? "subtitle1" : "h6"} fontWeight={600} color="text.secondary" gutterBottom>
            Loading Support Details
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
            Preparing your support channels...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (!org) {
    return (
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '80vh',
        background: 'linear-gradient(135deg, #F8FAFC 0%, #F1F5F9 100%)',
        p: 3
      }}>
        <Box
          sx={{
            p: { xs: 3, sm: 4 },
            borderRadius: 4,
            backgroundColor: alpha(theme.palette.error.main, 0.1),
            mb: 3
          }}
        >
          <FaHeadset size={isMobile ? 36 : 48} color={theme.palette.error.main} />
        </Box>
        <Typography variant={isMobile ? "h6" : "h5"} fontWeight={700} textAlign="center" gutterBottom color="text.primary">
          Support Unavailable
        </Typography>
        <Typography variant="body1" textAlign="center" color="text.secondary" sx={{ 
          mb: 4, 
          maxWidth: 400,
          fontSize: { xs: '0.875rem', sm: '1rem' }
        }}>
          We're experiencing temporary issues loading support information. Please try again shortly.
        </Typography>
        <Button
          variant="contained"
          size={isMobile ? "medium" : "large"}
          startIcon={<MdSupportAgent />}
          sx={{
            borderRadius: 3,
            px: { xs: 3, sm: 4 },
            py: { xs: 1, sm: 1.5 },
            fontWeight: 600,
            fontSize: { xs: '0.8rem', sm: '0.875rem' }
          }}
          onClick={() => window.location.reload()}
        >
          Retry Connection
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{
      background: 'linear-gradient(135deg, #F8FAFC 0%, #F1F5F9 100%)',
      minHeight: '100vh',
      fontFamily: "'Inter', -apple-system, sans-serif"
    }}>
      <Container maxWidth="lg" sx={{ py: { xs: 3, sm: 4, md: 6 }, px: { xs: 2, sm: 3 } }}>
        {/* Header Section */}
        <Box sx={{
          textAlign: 'center',
          maxWidth: 800,
          mx: 'auto',
          mb: { xs: 4, sm: 6, md: 8 }
        }}>
          <Chip
            icon={<FaShieldAlt />}
            label="Enterprise Support"
            variant="filled"
            sx={{
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              color: theme.palette.primary.main,
              fontWeight: 700,
              fontSize: { xs: '10px', sm: '12px' },
              mb: 4,
              px: 2,
              py: 1,
              height: { xs: 28, sm: 32 }
            }}
          />
          <Typography
            variant={isMobile ? (isSmallMobile ? "h4" : "h3") : "h2"}
            fontWeight={800}
            gutterBottom
            sx={{
              color: '#0F172A',
              letterSpacing: '-0.5px',
              background: 'linear-gradient(135deg, #0F172A 0%, #374151 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' }
            }}
          >
            Professional Support Center
          </Typography>
          <Typography
            variant={isMobile ? "body2" : "h6"}
            sx={{
              color: 'text.secondary',
              lineHeight: 1.6,
              maxWidth: 600,
              mx: 'auto',
              fontWeight: 500,
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }}
          >
            Dedicated enterprise support with guaranteed response times and 24/7 availability
            for your business communication needs.
          </Typography>
        </Box>

        {/* Main Organization Card */}
        <Card
          sx={{
            mb: { xs: 4, sm: 6, md: 8 },
            borderRadius: { xs: 3, sm: 4 },
            background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.light, 0.03)} 100%)`,
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            boxShadow: '0 8px 32px rgba(0,0,0,0.04)',
            overflow: 'hidden',
            position: 'relative'
          }}
        >
          <Box
            sx={{
              height: 6,
              background: 'linear-gradient(90deg, #25D366 0%, #128C7E 50%, #667eea 100%)',
              width: '100%'
            }}
          />
          <CardContent sx={{ p: { xs: 3, sm: 4, md: 6 } }}>
            <Box sx={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              alignItems: isMobile ? 'flex-start' : 'center',
              justifyContent: 'space-between',
              mb: { xs: 4, sm: 5 },
              gap: isMobile ? 3 : 0
            }}>
              <Box sx={{ flex: 1, width: '100%' }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: { xs: 2, sm: 3 }, 
                  mb: 3,
                  flexDirection: isSmallMobile ? 'column' : 'row',
                  textAlign: isSmallMobile ? 'center' : 'left'
                }}>
                  <Avatar
                    sx={{
                      width: { xs: 50, sm: 60 },
                      height: { xs: 50, sm: 60 },
                      backgroundColor: theme.palette.primary.main,
                      fontWeight: 700,
                      fontSize: { xs: '1.25rem', sm: '1.5rem' }
                    }}
                  >
                    {org.org_name?.charAt(0) || 'B'}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography 
                      variant={isMobile ? "h5" : "h4"} 
                      fontWeight={700} 
                      gutterBottom 
                      color="text.primary"
                      sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}
                    >
                      {org.org_name}
                    </Typography>
                    <Typography 
                      variant="body1" 
                      color="text.secondary" 
                      sx={{ 
                        fontWeight: 500,
                        fontSize: { xs: '0.875rem', sm: '1rem' }
                      }}
                    >
                      {org.description}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box sx={{ 
                display: 'flex', 
                gap: 2, 
                flexWrap: 'wrap',
                justifyContent: isMobile ? 'center' : 'flex-end',
                width: isMobile ? '100%' : 'auto'
              }}>
                <Chip
                  icon={<FaRocket />}
                  label="Fast Response"
                  variant="outlined"
                  sx={{ fontWeight: 600, fontSize: { xs: '0.7rem', sm: '0.8rem' } }}
                />
                <Chip
                  icon={<FaShieldAlt />}
                  label="24/7 Support"
                  variant="outlined"
                  sx={{ fontWeight: 600, fontSize: { xs: '0.7rem', sm: '0.8rem' } }}
                />
              </Box>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: { xs: 2, sm: 2 },
                  p: { xs: 2, sm: 3 },
                  borderRadius: 2,
                  backgroundColor: alpha(theme.palette.background.paper, 0.5),
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  mb: 2
                }}>
                  <Box sx={{ 
                    p: { xs: 1, sm: 1.5 }, 
                    borderRadius: 2, 
                    backgroundColor: alpha(theme.palette.primary.main, 0.1), 
                    flexShrink: 0 
                  }}>
                    <FaEnvelope size={isMobile ? 16 : 18} color={theme.palette.primary.main} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" fontWeight={600} color="text.primary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                      Email Support
                    </Typography>
                    <a 
                      href={`mailto:${org.support_email}`} 
                      style={{ 
                        color: theme.palette.primary.main, 
                        textDecoration: 'none', 
                        fontSize: isMobile ? '0.8rem' : '0.875rem',
                        wordBreak: 'break-word',
                        display: 'block'
                      }}
                    >
                      {org.support_email}
                    </a>
                  </Box>
                </Box>
                <InfoItem
                  icon={<FaGlobe />}
                  label="Website"
                  value={org.website}
                  isLink={true}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <InfoItem
                  icon={<FaMapMarkerAlt />}
                  label="Business Address"
                  value={org.address}
                />
                <InfoItem
                  icon={<FaClock />}
                  label="Working Hours"
                  value={org.working_hours}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Support Channels Section */}
        <Box sx={{ mb: { xs: 6, sm: 8 } }}>
          <Box sx={{ textAlign: 'center', mb: { xs: 4, sm: 6 } }}>
            <Typography
              variant={isMobile ? "h5" : "h4"}
              fontWeight={700}
              gutterBottom
              sx={{ color: 'text.primary' }}
            >
              Support Channels
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: 'text.secondary',
                maxWidth: 600,
                mx: 'auto',
                fontWeight: 500,
                fontSize: { xs: '0.875rem', sm: '1rem' }
              }}
            >
              Multiple dedicated channels for enterprise-grade support with guaranteed response times
            </Typography>
          </Box>

          <Grid container spacing={3} justifyContent="center">
            {org.support_type === "WhatsApp" && (
              <Grid item xs={12} sm={6} md={4}>
                <SupportChannelCard
                  icon={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FaWhatsapp size={isMobile ? 24 : 28} color="#25D366" />
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#25D366', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        WhatsApp
                      </Typography>
                    </Box>
                  }
                  title="WhatsApp Business"
                  subtitle="Instant messaging support with read receipts and media sharing for quick issue resolution"
                  action={() => window.open(`https://wa.me/${org.support_phone.replace(/[^0-9]/g, "")}`, "_blank")}
                  color="#25D366"
                  gradient="linear-gradient(135deg, #FFFFFF 0%, #F0FDF4 100%)"
                  badge="Fastest"
                />
              </Grid>
            )}

            <Grid item xs={12} sm={6} md={4}>
              <SupportChannelCard
                icon={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FaEnvelope size={isMobile ? 24 : 28} color="#2582d3ff" />
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#2582d3ff', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      Email
                    </Typography>
                  </Box>
                }
                title="Email Support"
                subtitle="Comprehensive ticket-based support with detailed tracking and documentation for complex issues"
                action={() => window.location = `mailto:${org.support_email}`}
                color={theme.palette.primary.main}
                gradient="linear-gradient(135deg, #FFFFFF 0%, #F0F9FF 100%)"
                badge="Detailed"
              />
            </Grid>
          </Grid>
        </Box>

        {/* Performance Metrics */}
        <Card
          sx={{
            borderRadius: { xs: 3, sm: 4 },
            background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.success.light, 0.05)} 100%)`,
            border: `1px solid ${alpha(theme.palette.success.main, 0.1)}`,
            p: { xs: 3, sm: 4, md: 5 }
          }}
        >
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant={isMobile ? "h6" : "h5"} fontWeight={700} gutterBottom color="text.primary">
                Enterprise Support Performance
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ 
                mb: 3, 
                lineHeight: 1.6,
                fontSize: { xs: '0.875rem', sm: '1rem' }
              }}>
                Our dedicated support team maintains exceptional service levels with guaranteed
                response times and 99.9% uptime for all business communication channels.
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                gap: { xs: 3, sm: 4 }, 
                flexWrap: 'wrap',
                justifyContent: isMobile ? 'space-around' : 'flex-start'
              }}>
                <Box sx={{ textAlign: isMobile ? 'center' : 'left' }}>
                  <Typography variant={isMobile ? "h5" : "h4"} fontWeight={800} color={theme.palette.success.main}>
                    &lt; 15min
                  </Typography>
                  <Typography variant="body2" color="text.secondary" fontWeight={500} sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    Avg. Response
                  </Typography>
                </Box>
                <Box sx={{ textAlign: isMobile ? 'center' : 'left' }}>
                  <Typography variant={isMobile ? "h5" : "h4"} fontWeight={800} color={theme.palette.success.main}>
                    99.9%
                  </Typography>
                  <Typography variant="body2" color="text.secondary" fontWeight={500} sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    Uptime SLA
                  </Typography>
                </Box>
                <Box sx={{ textAlign: isMobile ? 'center' : 'left' }}>
                  <Typography variant={isMobile ? "h5" : "h4"} fontWeight={800} color={theme.palette.success.main}>
                    24/7
                  </Typography>
                  <Typography variant="body2" color="text.secondary" fontWeight={500} sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    Availability
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  p: { xs: 3, sm: 4 },
                  borderRadius: 3,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.main, 0.05)} 100%)`,
                  textAlign: 'center',
                  border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`
                }}
              >
                <FiTrendingUp size={isMobile ? 36 : 48} color={theme.palette.success.main} />
                <Typography variant={isMobile ? "subtitle1" : "h6"} fontWeight={700} sx={{ mt: 2, color: 'text.primary' }}>
                  Premium Support
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                  Enterprise-grade reliability
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Card>
      </Container>
    </Box>
  );
};

export default SupportPage;